import React, { useCallback, useEffect, useRef, useState } from 'react';
import './InterviewForm.css'; // Import CSS for styles if needed
import { useNavigate, useParams } from 'react-router-dom';
import { useWindowSize } from './useWindowSize';
import axios from 'axios'; // Import axios
import logo from './assets/prompthire_logo.png';
import backgroundImage from './assets/Background.png'; // Import the background image
import HelpPopup from './RunDiagnostics'; // Import the HelpPopup component
import { useWebSocket } from './WebSocketContext';
import prettierLogo from './assets/Prettier_light 1(2).png'; // Import the new logo
import TermsModal from './T&C-popop'; // Import the T&C modal component
import { PromptHire } from './assets/svgComponent';
import PromptHireLogo from './assets/svgComponent/prompthireLogo.svg'; // Import the SVG logo
import { useToast } from './hooks/useToast';
import Terms11 from './assets/Terms1.1.pdf';
import DynamicColorSVG from './DynamicColorSVG';
import QRCode from "react-qr-code";
import InterviewGuidelinesModal from './InterviewGuidelinesModal'; // Import the InterviewGuidelinesModal component
import PermissionCheck from './components/PermissionCheckModal';
import JobOnHoldModal from './components/JobOnHoldModal/JobOnHoldModal'; // Import the JobOnHoldModal component
import WelcomeStep from './components/PermissionCheckModal/steps/WelcomeStep';
import MicrophoneStep from './components/PermissionCheckModal/steps/MicrophoneStep';
import CameraStep from './components/PermissionCheckModal/steps/CameraStep';
import BotInteractionStep from './components/PermissionCheckModal/steps/BotInteractionStep';
import GuidelinesStep from './components/PermissionCheckModal/steps/GuidelinesStep';
import logger from './utils/logger.js';

const BASE_URL = import.meta.env.VITE_APP_BASE_URL;
// import useMediaPermissions from './hooks/useMediaPermissions';
// import PermissionsModal from './components/PermissionsModal/PermissionsModal';

const InterviewForm = () => {
  const toast = useToast();
  const videoRef = useRef(null);
  const { width } = useWindowSize();
  const isTab = width < 1040;
  const { id } = useParams();
  const [gclbInitialized, setGclbInitialized] = useState(false);
  const navigate = useNavigate();
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [navLoading, setNavLoading] = useState(false);
  const [permissionsGranted, setPermissionsGranted] = useState(false);
  const [isChecked, setChecked] = useState(true)
  const [devices, setDevices] = useState({
    audioInputs: [],
    audioOutputs: [],
    videoInputs: []
  });
  const [selectedDevices, setSelectedDevices] = useState({
    audioInput: '',
    audioOutput: '',
    videoInput: ''
  });
  const [roundInfo, setRoundInfo] = useState({
    round_name: '',
    round_no: 0
  });
  const [showHelpPopup, setShowHelpPopup] = useState(false); // State to manage popup visibility
  const [showDiagnosticsPopup, setShowDiagnosticsPopup] = useState(false); // State to manage diagnostics popup visibility
  const [showTermsModal, setShowTermsModal] = useState(false); // State to manage T&C modal visibility
  const [showGuidelinesModal, setShowGuidelinesModal] = useState(false); // State to manage guidelines modal visibility
  
  // Initialize GCLB by calling the status API before using WebSocket
  useEffect(() => {
    const initializeGCLB = async () => {
      if (id && !gclbInitialized) {
        try {
          const response = await fetch(`https://${BASE_URL}/api/jobinterviews/status/${id}`, {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            logger.debug('GCLB initialized successfully');
            setGclbInitialized(true);
          } else {
            logger.error('Failed to initialize GCLB:', response.status);
          }
        } catch (error) {
          logger.error('Error initializing GCLB:', error);
        }
      }
    };
    
    initializeGCLB();
  }, [id]);
  
  // Only use WebSocket after GCLB is initialized
  const { ws, evaSpeaking, transcriptData, aiTranscript, socketData, handleSubmit } = useWebSocket();
  const [isActiveLoader, setActiveLoader] = useState(false)
  const [isJoinButtonClicked, setJoinButtonClicked] = useState(false); // New state to track button click
  const [orgMetadata, setOrgMetadata] = useState({
    logo: PromptHireLogo,
    colorCode: "#448df4"
  });

  const [screenshotDetected, setScreenshotDetected] = useState(false);
  const [interviewStatus, setInterviewStatus] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showJobOnHoldModal, setShowJobOnHoldModal] = useState(false);

  const handleKeyDown = (event) => {

    if (
      (event.metaKey && event.shiftKey && event.key === '3') ||
      (event.metaKey && event.shiftKey && event.key === '4') ||
      (event.metaKey && event.shiftKey && event.key === '5')
    ) {
      pressStartTime = Date.now();
    }

    if (event.key === 'PrintScreen' || (event.metaKey) || (event.key.startsWith('F') && !isNaN(parseInt(event.key.substring(1))))) {
      setScreenshotDetected(true);
    }
  };

  // const {
  //   hasMicPermission,
  //   hasCameraPermission,
  //   requestPermissions,
  //   isModalOpen,
  //   setIsModalOpen,
  //   stream
  // } = useMediaPermissions();

  useEffect(() => {
    // Attach the keydown event listener when the component mounts
    window.addEventListener('keydown', handleKeyDown);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const enterFullScreen = () => {
    const element = document.documentElement; // Fullscreen the entire document

    if (element.requestFullscreen) {
      element.requestFullscreen();
    } else if (element.mozRequestFullScreen) { // Firefox
      element.mozRequestFullScreen();
    } else if (element.webkitRequestFullscreen) { // Chrome, Safari, Opera
      element.webkitRequestFullscreen();
    } else if (element.msRequestFullscreen) { // IE/Edge
      element.msRequestFullscreen();
    }
  };

  useEffect(() => {
    if (socketData?.canJoin) {
      setValid(true)
    } else if (socketData?.isNavigate ) {
      // Store data in localStorage before navigation
      localStorage.setItem('room_url', socketData?.room_url);
      localStorage.setItem('job_interview_id', id);
      localStorage.setItem('userName', socketData?.user_name);

      navigate("/interview", {
        state: { room_url: socketData?.room_url, job_interview_id: id, userName: socketData?.user_name },
      });
    } else {
      logger.debug("Navigation not allowed."); // Optional: log or handle the case when navigation is not allowed
    }
  }, [socketData])

  const handleJoin = () => {
    if (!socketData?.canJoin) return;

    handleProceedAfterGuidelines();

    // if (!isChecked) {
    //   // Show error toast using our custom toast system
    //   toast.error("Please accept the Terms and Conditions to proceed.", 3000);
    //   // Reset the join button clicked state to allow another attempt
    //   setJoinButtonClicked(false);
    //   return;
    // }

    // Show guidelines modal instead of proceeding directly
    // setShowGuidelinesModal(true);
  };

  // Function to handle proceeding after guidelines are shown
  const handleProceedAfterGuidelines = () => {
    // Close the guidelines modal
    // setShowGuidelinesModal(false);
    enterFullScreen();

    // Only proceed if terms are accepted
    if (isActiveLoader) return;
    setActiveLoader(true);
    handleSubmit();
    // setJoinButtonClicked(true);
    // setTimeout(() => {
    //   setActiveLoader(false);
    // }, 5000);
  };

  const getMedia = async () => {
    try {
      const constraints = {
        video: {
          width: { ideal: isMobile ? 640 : 1280 },
          height: { ideal: isMobile ? 480 : 720 },
          facingMode: 'user',
          aspectRatio: { ideal: 1.333333 }, // 4:3 aspect ratio
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // Ensure video plays on mobile
        videoRef.current.play().catch(err => logger.error("Error playing video:", err));
      }
      setPermissionsGranted(true);
    } catch (error) {
      logger.error("Error accessing media devices.", error);
      toast.error("Could not access camera and microphone. Please check your permissions.", 5000);
      setPermissionsGranted(false);
    }
  };

  useEffect(() => {
    getMedia();

    // Cleanup function to stop the video stream when the component unmounts
    return () => {
      if (videoRef.current && videoRef.current.srcObject) {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach(track => track.stop());
      }
    };
  }, []);

  // Function to get all media devices
  const getConnectedDevices = async () => {
    try {
      // First request permission and get initial stream
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        },
        video: true
      });

      // Enumerate devices only after getting permission
      const devices = await navigator.mediaDevices.enumerateDevices();

      logger.debug('Raw devices:', devices);

      const deviceMap = {
        audioInputs: devices.filter(device => device.kind === 'audioinput'),
        audioOutputs: devices.filter(device => device.kind === 'audiooutput'),
        videoInputs: devices.filter(device => device.kind === 'videoinput')
      };

      // If no audio outputs are detected, add the default system output
      if (deviceMap.audioOutputs.length === 0) {
        deviceMap.audioOutputs = [{
          deviceId: 'default',
          label: 'System Default Speaker',
          kind: 'audiooutput'
        }];
      }

      setDevices(deviceMap);

      // Set default selected devices
      setSelectedDevices(prev => ({
        audioInput: deviceMap.audioInputs[0]?.deviceId || prev.audioInput,
        audioOutput: deviceMap.audioOutputs[0]?.deviceId || 'default',
        videoInput: deviceMap.videoInputs[0]?.deviceId || prev.videoInput
      }));

      // Update video stream with selected device
      if (deviceMap.videoInputs[0]) {
        updateVideoStream(deviceMap.videoInputs[0].deviceId);
      }

      // Stop the initial stream tracks
      stream.getTracks().forEach(track => track.stop());

    } catch (error) {
      logger.error('Error accessing media devices:', error);
      // Handle the error gracefully
      setDevices(prev => ({
        ...prev,
        audioOutputs: [{
          deviceId: 'default',
          label: 'System Default Speaker',
          kind: 'audiooutput'
        }]
      }));
    }
  };

  // Function to update video stream when device changes
  const updateVideoStream = async (deviceId) => {
    try {
      const constraints = {
        video: {
          deviceId: deviceId ? { exact: deviceId } : undefined,
          width: { ideal: isMobile ? 640 : 1280 },
          height: { ideal: isMobile ? 480 : 720 },
          facingMode: 'user',
          aspectRatio: { ideal: 1.333333 }, // 4:3 aspect ratio
        },
        audio: false // Since this is just for video
      };

      // Stop any existing streams first to avoid conflicts
      if (videoRef.current && videoRef.current.srcObject) {
        const oldStream = videoRef.current.srcObject;
        oldStream.getTracks().forEach(track => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;

        // Add a small delay before playing to ensure the video has loaded properly
        // This helps prevent the AbortError when sources change rapidly
        setTimeout(() => {
          if (videoRef.current) {
            // Check if the component is still mounted
            videoRef.current.play()
              .catch(err => {
                // Only log errors that aren't AbortError
                if (err.name !== 'AbortError') {
                  logger.error("Error playing video:", err);
                }
              });
          }
        }, 100); // 100ms delay before attempting to play
      }
    } catch (error) {
      logger.error('Error updating video stream:', error);
    }
  };

  // Handle device change
  const handleDeviceChange = async (type, deviceId) => {
    setSelectedDevices(prev => ({ ...prev, [type]: deviceId }));

    if (type === 'videoInput') {
      // Add debouncing to prevent rapid consecutive calls
      if (window.videoUpdateTimeout) {
        clearTimeout(window.videoUpdateTimeout);
      }

      window.videoUpdateTimeout = setTimeout(() => {
        updateVideoStream(deviceId);
      }, 200); // Small delay before updating video stream
    }
  };

  // Listen for device changes
  useEffect(() => {
    getConnectedDevices();

    // Listen for device changes (plug in/unplug)
    navigator.mediaDevices.addEventListener('devicechange', getConnectedDevices);

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', getConnectedDevices);

      // Clean up any existing video streams when component unmounts
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject;
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
      }

      // Clear any pending timeouts
      if (window.videoUpdateTimeout) {
        clearTimeout(window.videoUpdateTimeout);
      }
    };
  }, []);

  const getRoundInfo = async () => {
    try {
      const url = `https://${BASE_URL}/api/jobinterviews/${id}`; // Log the URL
      logger.debug("Fetching round info from:", url);

      const response = await axios.get(url); // Use axios to make the GET request

      // Check if the response is successful
      if (response.status === 200) {
        const data = response.data; // Access the data directly from the response
        logger.debug("Round Info:", data);

        setRoundInfo({
          round_name: data.round_name || 'Assessment',
          round_no: data.round_no || 1
        });
      } else {
        logger.error("Error fetching round info:", response.data);
      }
    } catch (error) {
      logger.error("Error fetching round info:", error);
    }
  };



  // Function to toggle the help popup
  const toggleHelpPopup = () => {
    setShowHelpPopup(prev => !prev);
  };

  // Function to toggle the diagnostics popup
  const toggleDiagnosticsPopup = () => {
    setShowDiagnosticsPopup(prev => !prev);
  };

  useEffect(() => {
    if (!id) {
      logger.error('ID is not available');
      return;
    }

    const fetchOrgMetadata = async () => {
      // Create an AbortController instance
      const abortController = new AbortController();

      try {
        const response = await fetch(`https://${BASE_URL}/api/organisation/meta-data/${id}`, {
          method: 'GET',
         // credentials: "include",
          signal: abortController.signal // Add the signal to the fetch request
        });

        if (response.status === 200) {
          const data = await response.json();
          setOrgMetadata({
            logo: data.logo_url || PromptHireLogo,
            colorCode: data.color?.primary || "#448df4",
          });
        } else {
          logger.error("Failed to fetch organization metadata:", response.status);
        }
      } catch (error) {
        // Only log error if it's not an abort error
        if (error.name !== 'AbortError') {
          logger.error("Error fetching organization metadata:", error);
        }
      }

      // Return cleanup function
      return () => {
        abortController.abort(); // Abort the fetch if component unmounts
      };
    };
    setTimeout(() => {
      fetchOrgMetadata();
    }, 2000);
    checkInterviewStatus();
  }, [id]);

  const checkInterviewStatus = async () => {
    try {
      const interviewId = id;
      const abortController = new AbortController();

      const response = await fetch(`https://${BASE_URL}/api/jobinterviews/status/${interviewId}`, {
        method: 'GET',
       // credentials: "include",
        headers: {
          'Content-Type': 'application/json'
        },
        signal: abortController.signal
      });

      if (response.status === 200) {
        const data = await response.json();
        logger.debug("Interview status response:", data); // Debug log

        // Store the status in state
        setInterviewStatus(data);



        if (data.jobopening_status != 'OPEN') {
          logger.debug("Job opening is not OPEN, showing job on hold modal");
          setShowJobOnHoldModal(true);
        }

        // Show modal for all statuses EXCEPT SCHEDULED and STARTED
        if (data && data.status &&
          data.status !== 'SCHEDULED' &&
          data.status !== 'STARTED') {
          logger.debug("Setting showStatusModal to true for status:", data.status);
          setShowStatusModal(true);
        } else {
          logger.debug("Status not requiring modal:", data.status);
          setShowStatusModal(false); // Explicitly hide modal for SCHEDULED and STARTED
        }

      } else {
        logger.error("Failed to fetch interview status:", response.status);
      }
    } catch (error) {
      // Only log error if it's not an abort error
      if (error.name !== 'AbortError') {
        logger.debug("here some error ocured")
        logger.error("Error fetching interview status:", error);
      }
    }
  };

  // Add a useEffect to check the interview status periodically
  useEffect(() => {
    // Check status immediately when component mounts
    checkInterviewStatus();

    // Set up interval to check status every 30 seconds
    const intervalId = setInterval(() => {
      checkInterviewStatus();
    }, 30000);

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [id]); // Only re-run if id changes

  // Add this line to check for mobile screens
  const isMobile = width < 700;

  // Add this useEffect to handle the custom event
  useEffect(() => {
    const handleHelpPopupClosed = () => {
      setShowDiagnosticsPopup(false);
    };

    window.addEventListener('helpPopupClosed', handleHelpPopupClosed);

    return () => {
      window.removeEventListener('helpPopupClosed', handleHelpPopupClosed);
    };
  }, []);


  return (
    <DynamicColorSVG color={orgMetadata.colorCode}>

      <div style={{
        height: '100vh',
        position: isMobile ? 'fixed' : 'static',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: isMobile ? 'linear-gradient(180deg, #F5F7FF 0%, #FFFFFF 100%)' : 'transparent',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {screenshotDetected && (
          <div style={{
            position: 'absolute',
            background: 'rgba(0, 0, 0, 0.8)',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            color: 'white',
            fontSize: '20px',
            flexDirection: 'column',
            gap: 12,
            backdropFilter: 'blur(8px)',  // This adds the blur effect
            WebkitBackdropFilter: 'blur(8px)' // For Safari support
          }}>
            <h3>You have pressed a restricted key (for screenshot), which is not allowed.</h3>
            <div onClick={() => setScreenshotDetected(false)} style={{ background: '#fff', padding: '6px 20px', borderRadius: 6, color: '#000', fontSize: 14, cursor: 'pointer' }}>
              Close
            </div>
          </div>
        )}
        {/* Main content wrapper */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: isMobile ? '20px' : '40px',
          position: 'relative',
          overflow: isMobile ? 'hidden' : 'visible'
        }}>
          {/* Header section */}
          <div style={{
            marginBottom: isMobile ? '0px' : '5px',
            position: 'relative'
          }}>
            <div style={{ height: '14%', padding: isMobile ? '10px' : '0', marginBottom: '30px' }}>
              <div style={{
                position: 'absolute',
                top: isMobile ? '5px' : '-20px',
                left: '-5px',
                width: 'auto',
                height: '28px'
              }}>
                <img
                  src={orgMetadata.logo}
                  alt="Organization Logo"
                  style={{
                    maxWidth: isMobile ? '100px' : '140px',
                    maxHeight: isMobile ? '30px' : '40px',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain'
                  }}
                />
              </div>
              <div className="absolute top-4" style={{
                right: '5px',
                width: 'auto',
                height: '28px',
                zIndex: 10  // Add zIndex to ensure button stays on top
              }}>
                {isMobile ? (
                  <button
                    className="flex items-center justify-center w-10 h-10 rounded-full"
                    onClick={toggleHelpPopup}
                    style={{
                      background: 'var(--White, #FFFFFF)',
                      border: '1px solid var(--White, #dadadd)',
                      boxShadow: '0px 2px 6px 0px #00000014',
                      transition: 'box-shadow 0.3s',
                      position: 'fixed',  // Change to fixed positioning
                      top: '20px',       // Adjust top position
                      right: '20px',     // Adjust right position
                    }}
                  >
                    <img
                      src="https://img.icons8.com/?size=100&id=8GKNsu0eFMQw&format=png&color=000000" // Question mark icon
                      alt="help icon"
                      className="w-5 h-5 object-contain"
                    />
                  </button>
                ) : (
                  <button
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full"
                    onClick={toggleHelpPopup}
                    style={{
                      background: 'var(--White, #FFFFFF)',
                      border: '1px solid var(--White, #dadadd)',
                      boxShadow: '0px 2px 6px 0px #00000014',
                      transition: 'box-shadow 0.3s',
                      position: 'fixed',  // Change to fixed positioning
                      top: '20px',       // Adjust top position
                      right: '20px',     // Adjust right position
                    }}
                  >
                    <span style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '14px',
                      fontWeight: 500,
                      lineHeight: '22px',
                      letterSpacing: '-0.2px',
                      textAlign: 'center',
                      textUnderlinePosition: 'from-font',
                      textDecorationSkipInk: 'none',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'clip'
                    }}>
                      Help & support
                    </span>
                    <img
                      src="https://img.icons8.com/?size=100&id=8GKNsu0eFMQw&format=png&color=000000"
                      alt="help icon"
                      className="w-5 h-5 object-contain"
                    />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Title section */}
          {/* <div className="mb-6" style={{ marginLeft: isMobile ? '0' : '80px' }}>
            <h1 style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: isMobile ? '18px' : '16px',
              fontWeight: isMobile ? '600' : '400',
              textAlign: isMobile ? 'center' : 'left'
            }}>
              Round 1 — General Assessment
            </h1>
            <p style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: isMobile ? '13px' : '16px',
              color: '#666',
              textAlign: isMobile ? 'center' : 'left'
            }}>
              Approximately 30 mins assessment
            </p>
          </div> */}

      


          <PermissionCheck
            onComplete={() => { }}
            startWithScreenSharing={false}
            interviewId={id}
            className="interview-permission-check"
            handleJoin={handleJoin}
            isActiveLoader={isActiveLoader}
            interviewStatus={interviewStatus}
            isMobile={isMobile}
            enterFullScreen={enterFullScreen}
            socketData={socketData}
            permissionsGranted={permissionsGranted}
            steps={[
              { id: 'welcome', component: WelcomeStep },
              { id: 'microphone', component: MicrophoneStep },
              { id: 'camera', component: CameraStep },
              { id: 'botInteraction', component: BotInteractionStep },
              { id: 'guidelines', component: GuidelinesStep }
              // Screen sharing step removed as requested
            ]}
          />
        </div>

        {/* Modals */}
        {showHelpPopup && (
          <div
            className="absolute"
            style={{
              width: '210px', // Fill width
              height: 'auto', // Adjust height to hug content
              top: '60px', // Adjusted position to be below the help button
              right: '40px', // Align with the button
              padding: '8px 4px', // Adjusted padding
              gap: '2px',
              borderRadius: '10px', // Adjust border radius as needed
              border: '1px solid #ccc', // Light border for visibility
              backgroundColor: 'white', // Background color for the popup
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)', // Optional shadow for better visibility
              overflow: 'hidden', // Prevent overflow
              marginTop: '8px', // Add margin-top to create space between the popup and the button
            }}
          >
            <div style={{
              width: '194px', // Set width for the contact support box
              height: 'auto', // Set height to auto to fit content
              gap: '0px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '24px',
              letterSpacing: '-0.3px',
              textAlign: 'left',
              textUnderlinePosition: 'from-font',
              textDecorationSkipInk: 'none',
              padding: '4px 0', // Padding for the text
            }}>
              <p style={{ margin: '0', display: 'inline' }}>Contact Support</p>
            </div>
            <div style={{
              width: '194px', // Fill width
              height: 'auto', // Set height to auto to fit content
              gap: '4px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontWeight: 400, // Updated font weight
              lineHeight: '22px', // Updated line height
              letterSpacing: '-0.2px', // Updated letter spacing
              padding: '4px 0', // Padding for the text
            }}>
              <div className="flex items-center gap-2" style={{ display: 'flex', alignItems: 'center' }}>
                <img
                  src="https://img.icons8.com/material-outlined/24/0000FF/email-open.png" // Blue email icon
                  alt="email icon"
                  className="w-4 h-4"
                />
                <p style={{ margin: '0', overflowWrap: 'break-word', color: 'var(--Gray-600, #5F5F6A)' }}> Interview@prompthire.ai</p>
              </div>
              <div className="flex items-center gap-2">
                <img
                  src="https://img.icons8.com/material-outlined/24/0000FF/phone.png" // Blue phone icon
                  alt="phone icon"
                  className="w-4 h-4"
                />
                <p style={{ margin: '0', overflowWrap: 'break-word', color: 'var(--Gray-600, #5F5F6A)' }}> (+91) 79790 23221 </p>
              </div>
            </div>
            {/* Gray line between phone and Run Diagnostics */}
            <div style={{
              height: '1px',
              backgroundColor: '#ccc',
              margin: '4px 0', // Add some margin for spacing
            }} />
            {/* Run Diagnostics Button */}
            <button
              style={{
                width: '200px', // Fill width
                height: '30px', // Hug height
                padding: '4px 8px',
                gap: '8px',
                borderRadius: '6px', // Set border radius for rounded corners
                backgroundColor: '', // Default background color
                textAlign: 'center',
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '22px',
                letterSpacing: '-0.2px',
                marginTop: '4px', // Add some margin for spacing
                transition: 'background-color 0.3s, border-radius 0.3s', // Smooth transition for hover effect
                color: 'var(--Gray-600, #5F5F6A)', // Change text color
              }}
              onClick={toggleDiagnosticsPopup} // Open diagnostics popup on click
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.1)'; // Change background on hover
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = ''; // Revert background
              }}
            >
              Run Diagnostics
            </button>

          </div>
        )}

        {showDiagnosticsPopup && (
          <div style={{
            position: 'fixed',
            top: isMobile ? '69px' : '',
            left: isMobile ? '16px' : '',
            width: isMobile ? '328px' : '',
            height: isMobile ? '555px' : '',
            transform: isMobile ? 'none' : '',
            borderRadius: '12px',
            zIndex: 1000,
            background: 'white',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
          }}>
            <HelpPopup />
          </div>
        )}
        {showTermsModal && (
          <TermsModal
            onAccept={handleAcceptTerms}
            onClose={() => {
              logger.debug("Modal closing"); // Debug log
              setShowTermsModal(false);
            }}
          />
        )}
        {showJobOnHoldModal && (
          <JobOnHoldModal 
            isOpen={showJobOnHoldModal}
            onClose={() => setShowJobOnHoldModal(false)}
            isMobile={isMobile}
          />
        )}
        {showStatusModal && interviewStatus && (
          <div style={{
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: isMobile ? 'flex-start' : 'flex-start',
            justifyContent: 'center',
            zIndex: 2000,
            fontFamily: 'Inter, sans-serif',
            animation: 'fadeIn 0.3s ease-out',
            paddingTop: isMobile ? '40px' : '50px', // Reduced padding to shift content higher
          }}>
            <div style={{
              width: isMobile ? '90%' : '550px',
              height: isMobile ? 'auto' : '500px', // Increased overall height from 450px to 500px
              maxHeight: isMobile ? '80vh' : 'auto',
              position: 'relative',
              backgroundColor: 'rgba(255, 255, 255, 1)',
              borderRadius: '12px',
              padding: isMobile ? '20px' : '28px',
              boxShadow: '0px 25px 50px -12px rgba(0, 0, 0, 0.25), 0px 0px 0px 1px rgba(17, 24, 39, 0.1)',
              animation: 'slideDown 0.4s ease-out',
              display: 'flex',
              flexDirection: 'column',
              overflowY: isMobile ? 'auto' : 'visible',
            }}>
              {/* Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px' // Increased from 12px
              }}>
                <h2 style={{
                  fontSize: isMobile ? '20px' : '22px',
                  fontWeight: '400',
                  margin: 0
                }}>Feedback</h2>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  {!isMobile && (
                    <span style={{
                      display: 'flex',
                      height: '30px',
                      width: '40px',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '6px',
                      border: '1px solid #e2e8f0',
                      fontSize: '14px',
                      color: '#718096'
                    }}>
                      esc
                    </span>
                  )}
                  <button
                    onClick={() => setShowStatusModal(false)}
                    style={{
                      display: 'flex',
                      height: '32px',
                      width: '32px',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '6px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f7fafc'}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Content - keeping the same size for the red area */}
              <div style={{
                marginTop: '16px', // Increased from 12px
                borderRadius: '8px',
                background: 'linear-gradient(180deg, #FEF2F2 0%, rgba(254, 242, 242, 0.7) 100%)',
                padding: isMobile ? '20px' : '24px',
                marginBottom: '24px' // Increased from 20px
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: isMobile ? '12px' : '16px'
                }}>
                  {/* Error Icon */}
                  <div style={{
                    display: 'flex',
                    height: isMobile ? '45px' : '55px',
                    width: isMobile ? '45px' : '55px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    background: 'linear-gradient(to bottom, #f87171, #dc2626)',
                    color: 'white'
                  }}>
                    <span style={{
                      fontSize: isMobile ? '26px' : '34px',
                      fontWeight: '700'
                    }}>!</span>
                  </div>

                  {/* Error Title */}
                  <h2 style={{
                    textAlign: 'center',
                    fontSize: isMobile ? '18px' : '22px',
                    fontWeight: '500',
                    color: '#991b1b',
                    margin: 0
                  }}>
                    You've already appeared for this interview!
                  </h2>

                  {/* Error Description */}
                  <p style={{
                    textAlign: 'center',
                    fontSize: isMobile ? '14px' : '16px',
                    color: '#4b5563',
                    margin: 0,
                    lineHeight: '1.5'
                  }}>
                    Looks like you've already appeared for this interview. Our team must be grading your performance at this moment.
                  </p>
                </div>
              </div>

              {/* Contact Information - now as a separate section with more space */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: isMobile ? '12px' : '16px',
                width: '100%',
                padding: isMobile ? '16px 0' : '24px 0',
                borderTop: '1px solid rgba(0,0,0,0.1)',
                marginBottom: '30px', // Added more space before the button
                flex: '1' // This will make this section expand to fill available space
              }}>
                <p style={{
                  fontSize: isMobile ? '15px' : '17px',
                  fontWeight: '500',
                  color: '#4b5563',
                  margin: '0'
                }}>
                  For any queries, please contact us:
                </p>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: isMobile ? '14px' : '15px',
                  color: '#4b5563',
                  padding: '6px 0' // Increased padding
                }}>
                  <img
                    src="https://img.icons8.com/material-outlined/24/0000FF/email-open.png"
                    alt="email icon"
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span>Interview@prompthire.info</span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '7px',
                  fontSize: isMobile ? '14px' : '15px',
                  color: '#4b5563',
                  padding: '6px 0' // Increased padding
                }}>
                  <img
                    src="https://img.icons8.com/material-outlined/24/0000FF/phone.png"
                    alt="phone icon"
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span>(+91) 79790 23221</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Interview Guidelines Modal */}
        <InterviewGuidelinesModal
          isOpen={showGuidelinesModal}
          onClose={handleProceedAfterGuidelines}
        />
      </div>
    </DynamicColorSVG>
  );
};

export default InterviewForm;
